import React, { Children, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

function optionsFrom(children, group = '') {
  return Children.toArray(children).flatMap(child => {
    if (!React.isValidElement(child)) return [];
    if (child.type === 'optgroup') return optionsFrom(child.props.children, child.props.label);
    return [{ value: String(child.props.value ?? ''), label: child.props.children, group, disabled: child.props.disabled }];
  });
}

export default function CartoonSelect({ children, value, onChange, disabled, id }) {
  const uid = useId();
  const trigger = useRef(null), popup = useRef(null), search = useRef(null);
  const revealActive = useRef(true);
  const [open, setOpen] = useState(false), [query, setQuery] = useState(''), [active, setActive] = useState(0);
  const [position, setPosition] = useState({}), [label, setLabel] = useState('');
  const options = optionsFrom(children);
  const selected = options.find(option => option.value === String(value));
  const filtered = options.filter(option => String(option.label).toLocaleLowerCase('vi').includes(query.toLocaleLowerCase('vi')));
  useEffect(() => {
    const parent = trigger.current.closest('label');
    setLabel(parent ? Array.from(parent.childNodes).filter(node => node.nodeType === 3).map(node => node.textContent.trim()).join(' ') : 'Chọn một mục');
  }, []);
  useLayoutEffect(() => {
    if (!open) return;
    const place = event => {
      // A scroll inside the popup changes neither its anchor nor its placement.
      if (event?.target instanceof Node && popup.current?.contains(event.target)) return;
      const rect = trigger.current.getBoundingClientRect();
      const below = window.innerHeight - rect.bottom - 16;
      const above = rect.top - 16;
      const up = below < 260 && above > below;
      const width = Math.min(Math.max(rect.width, 280), window.innerWidth - 16);
      setPosition({ position: 'fixed', left: Math.max(8, Math.min(rect.left, window.innerWidth - width - 8)), width, maxHeight: Math.min(360, up ? above : below), ...(up ? { bottom: window.innerHeight - rect.top + 8 } : { top: rect.bottom + 8 }) });
    };
    const outside = event => { if (!popup.current?.contains(event.target) && !trigger.current.contains(event.target)) setOpen(false); };
    place();
    search.current?.focus({ preventScroll: true });
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    document.addEventListener('pointerdown', outside);
    return () => { window.removeEventListener('resize', place); window.removeEventListener('scroll', place, true); document.removeEventListener('pointerdown', outside); };
  }, [open]);
  useEffect(() => { if (disabled) setOpen(false); }, [disabled]);
  useLayoutEffect(() => {
    if (!open || !revealActive.current) return;
    const list = popup.current?.querySelector('.tt-select-options');
    const option = document.getElementById(`${uid}-${active}`);
    if (!list || !option) return;
    const bounds = list.getBoundingClientRect();
    const item = option.getBoundingClientRect();
    // Scroll only the list, never the document or an ancestor form.
    if (item.top < bounds.top) list.scrollTop -= bounds.top - item.top;
    else if (item.bottom > bounds.bottom) list.scrollTop += item.bottom - bounds.bottom;
  }, [active, open, uid, query, position]);
  const choose = option => {
    if (!option || option.disabled) return;
    onChange?.({ target: { value: option.value } });
    setOpen(false); trigger.current.focus({ preventScroll: true });
  };
  const show = () => { revealActive.current = true; setQuery(''); setActive(Math.max(0, options.findIndex(option => option.value === String(value)))); setOpen(true); };
  const keys = event => {
    if (event.key === 'Escape') { event.preventDefault(); setOpen(false); trigger.current.focus({ preventScroll: true }); }
    if (event.key === 'Tab') { setOpen(false); trigger.current.focus({ preventScroll: true }); }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) { show(); return; }
      const step = event.key === 'ArrowDown' ? 1 : -1;
      let next = active;
      for (let count = 0; count < filtered.length; count++) {
        next = (next + step + filtered.length) % filtered.length;
        if (!filtered[next].disabled) break;
      }
      revealActive.current = true;
      setActive(next);
    }
    if (event.key === 'Enter' && open) { event.preventDefault(); choose(filtered[active]); }
  };
  return <>
    <button ref={trigger} id={id} type="button" className="tt-select-trigger" disabled={disabled} aria-label={label ? `${label}: ${selected?.label || 'Chọn một mục'}` : undefined} aria-haspopup="listbox" aria-expanded={open} aria-controls={open ? uid : undefined} onKeyDown={keys} onClick={() => open ? setOpen(false) : show()}>
      <span title={typeof selected?.label === 'string' ? selected.label : undefined}>{selected?.label || 'Chọn một mục'}</span><span aria-hidden="true">⌄</span>
    </button>
    {open && createPortal(<div ref={popup} className="tt-ui tt-select-popup" style={{ position: 'fixed', ...position }} onKeyDown={keys}>
      <input ref={search} className="tt-select-search" role="combobox" aria-label={`Tìm trong ${label || 'danh sách'}`} aria-expanded="true" aria-controls={uid} aria-activedescendant={filtered[active] ? `${uid}-${active}` : undefined} placeholder="Tìm nhanh…" value={query} onChange={event => { revealActive.current = true; setQuery(event.target.value); setActive(0); }} />
      <div role="listbox" id={uid} aria-label={label || 'Danh sách lựa chọn'} className="tt-select-options">
        {filtered.map((option, index) => <React.Fragment key={option.value}>
          {option.group && option.group !== filtered[index - 1]?.group && <div className="tt-select-group">{option.group}</div>}
          <div role="option" id={`${uid}-${index}`} aria-selected={option.value === String(value)} aria-disabled={!!option.disabled} className={`tt-select-option ${index === active ? 'is-active' : ''}`} onPointerMove={() => { revealActive.current = false; setActive(index); }} onMouseDown={event => event.preventDefault()} onClick={() => choose(option)}>
            <span>{option.label}</span><span aria-hidden="true">{option.value === String(value) ? '✓' : ''}</span>
          </div>
        </React.Fragment>)}
        {!filtered.length && <div className="tt-select-empty" role="status">Không tìm thấy mục phù hợp.</div>}
      </div>
    </div>, document.body)}
  </>;
}
